import {DestroyRef, inject, Injectable} from '@angular/core';
import {UserService} from "./user.service";
import {take} from "rxjs/operators";
import {WorkDialogComponent} from "../work/work-dialog/work-dialog.component";
import {MatDialog} from "@angular/material/dialog";
import {MatSnackBar} from "@angular/material/snack-bar";
import {PrioritiesService} from "../priorities/priorities.service";
import { FirebaseService } from './firebase.service';
import { firstValueFrom, fromEvent, Subject } from 'rxjs';
import { UserDeviceService } from './user-device.service';
import { ChoiceDialogComponent } from '../shared/dialogs/choice-dialog/choice-dialog.component';
import { goToDetails, GoToDetailsArgs } from '../utils/navigation/go-to-details';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IndexedDBService } from './indexed-db.service';
import { Capacitor } from '@capacitor/core';
import { PushNotifications, Token, PushNotificationSchema, ActionPerformed } from '@capacitor/push-notifications';

const DO_NOT_SHOW_DISABLED_NOTIFICATION_WARNING = 1;
const ENABLE_NOTIFICATIONS = 0;

@Injectable({
  providedIn: 'root'
})
export class MessagingService {
  listenSub: any;
  fallbackListenSub: any;
  messagingToken$: Subject<string> = new Subject<string>();
  user:any;

  private readonly destroyRef = inject(DestroyRef);
  private readonly indexedDBService = inject(IndexedDBService);

  constructor(
    private userService: UserService,
    private firebase: FirebaseService,
    private matDialog: MatDialog,
    private snackBar: MatSnackBar,
    private prioritiesService: PrioritiesService,
    private userDeviceService: UserDeviceService
  ) { }
  init(){
    if(Capacitor.isNativePlatform()){
      console.log('[FCM] Native Platform detected, skipping FCM init');
      this.mobilePush()
    }else{
      this.requestToken()
    }
    this.userService.user$.subscribe(value => {
      if(value){
        this.user = value;
        this.fallbackListen();
      }else if(this.listenSub){
        this.deleteToken();
      }
    });
  }
  mobilePush(){
    PushNotifications.addListener('registration', (token: Token) => {
      console.log('[FCM] Device Token: ' + token.value);
      this.updateToken(token.value);
    });

    // Some issue with our setup and push will not work
    PushNotifications.addListener('registrationError', (error: any) => {
      console.log('Error on registration: ' + JSON.stringify(error));
    });

    // Show us the notification payload if the app is open on our device
    PushNotifications.addListener('pushNotificationReceived', (notification: PushNotificationSchema) => {
      this.firebase.messages$.next(notification);
      this.processMessage(notification);
      console.log('Push received: ' + JSON.stringify(notification));
    });

    // Method called when tapping on a notification
    PushNotifications.addListener('pushNotificationActionPerformed', (notification: ActionPerformed) => {
      console.log('Push action performed: ' + JSON.stringify(notification));
    });
    this.requestPermission();
  }
  requestPermission() {
    if(Capacitor.isNativePlatform()){
      console.log('[FCM] Requesting Push Notification Permission');
      PushNotifications.checkPermissions().then((result) => {
        if(result.receive === 'prompt'){
          PushNotifications.requestPermissions().then((result) => {
            if (result.receive === 'granted') {
              // Register with Apple / Google to receive push via APNS/FCM
              PushNotifications.register();
            } else {
              // Show some error
            }
          });
        }else if(result.receive === 'granted'){
          PushNotifications.register();
        }
      });
    }else{
      Notification.requestPermission().then((permission) => {
        if(permission === 'granted'){
          console.log('[FCM] Notification Permission granted');
          this.requestToken();
        }else{
          console.log('[FCM] Notification Permission denied');
        }
      }).catch((error) => {
        console.log(error);
      });
    }
  }
  requestToken(){
    if(Notification.permission == 'granted'){
      console.log('[FCM] Requesting Device Token');
      this.firebase.getMesssagingToken()
        .then((token) => {
          this.updateToken(token);
        })
        .catch((error) => {
          console.log(error);
        });
    } else if (Notification.permission === 'default') {
      this.matDialog.open(
        ChoiceDialogComponent,
        {
          data: {
            title: '¡Advertencia!',
            body: 'Para el correcto funcionamiento de ControlCar debes activar las notificaciones en tu navegador y recargar la página',
            choices: ['Activar notificaciones'],
          }
        }
      ).afterClosed().pipe(take(1)).subscribe((choice: number) => {
        if (choice === ENABLE_NOTIFICATIONS) {
          this.requestPermission();
        }
      });
    } else {
      const showNotification = localStorage.getItem('showDisabledNotificationWarning') !== 'false';
      if (!showNotification) return;

      this.matDialog.open(
        ChoiceDialogComponent,
        {
          data: {
            title: '¡Advertencia!',
            body: 'Hemos detectado que tienes las notificaciones desactivadas, para el correcto funcionamiento de ControlCar debes activarlas',
            choices: [{ text: 'Entendido' }, {text: 'No volver a mostrar. Entiendo los riesgos.', class: 'warn'}],
          }
        }
      ).afterClosed().pipe(take(1)).subscribe((choice: number) => {
        if (choice === ENABLE_NOTIFICATIONS) {
          this.requestPermission();
        } else if (choice === DO_NOT_SHOW_DISABLED_NOTIFICATION_WARNING) {
          localStorage.setItem('showDisabledNotificationWarning', 'false');
        }
      });
    }
  }
  updateToken(token:string) {
    this.userService.setDeviceToken(token);
    this.messagingToken$.next(token);
    firstValueFrom(this.userDeviceService.update({
      notification_token: token,
    })).then(() => {
      console.log('[FCM] Device Token Updated');
    });
  }
  deleteToken() {
    this.firebase.deleteToken().then(() => {
      this.listenSub.unsubscribe();
    });
  }
  playAudio(){
    try{
      const audio = new Audio('assets/sounds/alert.mp3');
      audio.play();
    }catch(e){
      console.log(e);
    }
  }
  fallbackListen(){
    if(this.fallbackListenSub){
      this.fallbackListenSub.unsubscribe();
    }
    const lastNotificationAt = localStorage.getItem('last_notification_at');
    const route = `notifications/${this.user.id}`;
    this.fallbackListenSub = this.firebase.objectAdded(route, lastNotificationAt).subscribe((message:any) => {
      console.log('[FCM] Message received:', message);
      this.firebase.messages$.next(message);
      this.processMessage(message);
    });

    // Process messages that were received while the app was in the background
    fromEvent(window, 'focus').pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      this.indexedDBService.read('background_notifications').then((value) => {
        if (!value || value.length === 0) {
          return;
        }
        value.forEach((message) => {
          this.processMessage(message);
          this.indexedDBService.delete('background_notifications', message.id);
        });
      });
    });
    console.log('[FCM] Fallback Listening Enabled');
  }

  removeFallbackMessage(message:any){
    this.firebase.removeObject(`notifications/${this.user.id}/${message.id}`).then(()=>{
    }).catch();
  }

  processMessage(message:any){
    if (!message.data) return;

    if(message.data.user_id && message.data.user_id !== this.user.id){
      console.log('[FCM] Message not for this user, ignoring');
      return;
    }

    else if(message.data.type === 'comment-reply'){
      
    }
    else if(message.notification){
      this.playAudio();
      this.openSnackBar(message);  
    }
    else if(message.data.type === 'work-request'){
      this.playAudio();
      this.snackBar.open('Se ha enviado la solicitud de trabajo', 'Ver', {
        duration: 10000,

      }).onAction().pipe(take(1)).subscribe(()=>{
        const data = JSON.parse(message.data.payload);
        this.matDialog.open(WorkDialogComponent,{data: {id: data.id, type: 'WorkInstance'}, minWidth: '80%'});
      });
    }

    if (message.data.created_at) {
      localStorage.setItem('last_notification_at', message.data.created_at);
    }
  }

  openDialog(notification:any){
    let goToDetailsParams: GoToDetailsArgs = {
      id: notification.id,
      type: notification.type,
      dialog: this.matDialog,
      mode: 'modal',
    };
    switch(notification.model_class){
      case 'OrderComment':
        goToDetailsParams['id'] = notification.content;
        goToDetailsParams['type'] = 'Order';
        break;
      case 'Comment':
        goToDetailsParams['id'] = notification.content;
        goToDetailsParams['type'] = notification.notification_type;
        break;
      case 'Event':
        goToDetailsParams['type'] = notification.notification_type === 'info'
          ? 'Order'
          : notification.notification_type;
        break;
    }
    goToDetails(goToDetailsParams);
  }

  openSnackBar(message:any) {
    this.snackBar.open(message.notification.body, 'Ver', {
      duration: 10000,
    }).onAction().pipe(take(1)).subscribe(()=>{
      const data = JSON.parse(message.data.payload);
      // TODO: Check if we can remove priorities service 
      // this.prioritiesService.complete(data.id);
      this.openDialog(data);
    });
  }
}
